import { useCallback, useEffect, useState } from 'react';
import { post } from '../../lib/api.js';

/**
 * The invitation landing screen — what a mailed "Join Phibian" link opens.
 *
 * Renders PRE-SESSION, like the organization chooser, because the whole point is
 * that this person does not have an account yet. It is matched against
 * `window.location.pathname` before the router mounts (see App.tsx and
 * lib/invitePath.ts).
 *
 * Deliberately built from the existing `signin-*` classes rather than a new set:
 * this is the same moment in the same product as the sign-in screen, and it
 * should not look like a different application. Only the handful of elements
 * sign-in does not have — the provider divider and the stacked form fields —
 * introduce new class names.
 *
 * The screen holds no authority of its own. Everything it shows comes from
 * `POST /auth/invitation/lookup`, which resolves the token server-side; and the
 * organization, role and email it displays are all things the invitation email
 * already told this person. Nothing it renders is trusted on the way back:
 * acceptance re-resolves the token and takes the role from the invitation row,
 * never from this client.
 *
 * Four dead ends get their own words rather than one generic failure. Someone
 * whose link expired needs to be told to ask for a new one; someone whose
 * invitation was withdrawn needs to talk to their administrator; someone who
 * already accepted needs the sign-in page. Collapsing those into "invalid link"
 * reads like the product is broken and generates a support conversation every
 * time.
 */

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: InvitePreview }
  | { kind: 'dead'; state: DeadState; invite?: InvitePreview };

type DeadState = 'expired' | 'revoked' | 'accepted' | 'unknown' | 'unreachable';

interface InvitePreview {
  state: string;
  organizationName: string;
  roleName: string;
  email: string;
  invitedName: string | null;
  expiresAt: string;
  passwordSignupAvailable: boolean;
}

const DEAD_COPY: Record<DeadState, { title: string; body: string; action?: string }> = {
  expired: {
    title: 'This invitation has expired',
    body: 'Invitations are time-limited for security. Ask whoever invited you to send a new one — it only takes them a moment.',
  },
  revoked: {
    title: 'This invitation was withdrawn',
    body: 'An administrator at the organization cancelled this invitation. If you think that was a mistake, contact them directly.',
  },
  accepted: {
    title: 'This invitation has already been used',
    body: 'An account was already set up with this link. If that was you, sign in normally.',
    action: 'Go to sign in',
  },
  unknown: {
    title: "This invitation link isn't valid",
    body: 'The link may have been copied incompletely. Try opening it again directly from the email, or ask for a new invitation.',
  },
  unreachable: {
    title: "We couldn't check this invitation",
    body: 'Something went wrong on our side rather than yours. Reload the page in a moment — the link itself should still be fine.',
    action: 'Reload',
  },
};

function formatExpiry(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  const days = Math.max(0, Math.round((when.getTime() - Date.now()) / 86_400_000));
  if (days === 0) return 'This link expires today.';
  if (days === 1) return 'This link expires tomorrow.';
  return `This link expires in ${days} days.`;
}

/** Initials for the organization chip, matching the sign-in screen's treatment. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function AcceptInvite({ token }: { readonly token: string }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    post<InvitePreview>('/auth/invitation/lookup', { token })
      .then((invite) => {
        if (cancelled) return;
        if (invite.state === 'valid') {
          setPhase({ kind: 'ready', invite });
          setName(invite.invitedName ?? '');
        } else {
          setPhase({ kind: 'dead', state: invite.state as DeadState, invite });
        }
      })
      .catch((err: Error & { status?: number; body?: { state?: string } }) => {
        if (cancelled) return;
        // A 404 carries the specific reason. Anything else is our problem, not
        // the recipient's, and must not be described to them as a bad link.
        setPhase({
          kind: 'dead',
          state: err.status === 404 ? ((err.body?.state as DeadState) ?? 'unknown') : 'unreachable',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitPassword = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);

      // Checked here as well as server-side so the common mistakes cost no
      // round trip — and, for the mismatch, so the message can name the actual
      // problem rather than a generic rejection.
      if (password !== confirm) {
        setError('Those two passwords do not match.');
        return;
      }
      if (password.length < 8) {
        setError('Use at least 8 characters.');
        return;
      }

      setSubmitting(true);
      try {
        await post('/auth/invitation/accept', {
          token,
          password,
          ...(name.trim() ? { name: name.trim() } : {}),
        });
        // A full page load, not a client transition: the app must boot from a
        // fresh /auth/me against the newly minted session cookie.
        window.location.assign('/');
      } catch (err) {
        setError((err as Error).message || 'We could not complete sign-up. Try again.');
        setSubmitting(false);
      }
    },
    [confirm, name, password, token],
  );

  if (phase.kind === 'loading') {
    return <main className="splash">Checking your invitation…</main>;
  }

  if (phase.kind === 'dead') {
    const copy = DEAD_COPY[phase.state];
    return (
      <main className="signin-panel">
        <div className="signin-wrap">
          <div className="signin-card">
            <h1 className="signin-h2">{copy.title}</h1>
            <p className="signin-sub">{copy.body}</p>
            {phase.invite ? (
              <div className="signin-org">
                <span className="signin-org-avatar">{initials(phase.invite.organizationName)}</span>
                <span className="signin-org-copy">
                  <span className="signin-org-label">Invitation for</span>
                  <span className="signin-org-name">{phase.invite.email}</span>
                </span>
              </div>
            ) : null}
            {copy.action ? (
              <a className="signin-primary" href="/">
                {copy.action}
              </a>
            ) : null}
          </div>
        </div>
      </main>
    );
  }

  const invite = phase.invite;
  const googleHref = `/auth/invitation/google/start?token=${encodeURIComponent(token)}`;

  return (
    <main className="signin-panel">
      <div className="signin-wrap">
        <div className="signin-card">
          <h1 className="signin-h2">Join {invite.organizationName}</h1>
          <p className="signin-sub">
            You&rsquo;ve been invited to Phibian as <strong>{invite.roleName}</strong>. Set up how
            you&rsquo;ll sign in, and you&rsquo;re in.
          </p>

          <div className="signin-org">
            <span className="signin-org-avatar">{initials(invite.organizationName)}</span>
            <span className="signin-org-copy">
              <span className="signin-org-label">Joining as</span>
              <span className="signin-org-name">{invite.email}</span>
            </span>
          </div>

          {/* Google first: one click, and nothing new to remember. */}
          <a className="signin-primary" href={googleHref}>
            Continue with Google
          </a>

          {invite.passwordSignupAvailable ? (
            <>
              <div className="invite-divider">
                <span>or create a password</span>
              </div>

              <form onSubmit={submitPassword}>
                <label className="invite-field">
                  <span className="invite-label">Your name</span>
                  <input
                    className="signin-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    placeholder="Sam Ortiz"
                    maxLength={120}
                  />
                </label>

                <label className="invite-field">
                  <span className="invite-label">Password</span>
                  <input
                    className="signin-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    aria-describedby="invite-password-hint"
                  />
                </label>
                <p className="invite-hint" id="invite-password-hint">
                  At least 8 characters.
                </p>

                <label className="invite-field">
                  <span className="invite-label">Confirm password</span>
                  <input
                    className="signin-input"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                  />
                </label>

                {error ? (
                  <p className="signin-err" role="alert">
                    {error}
                  </p>
                ) : null}

                <button className="signin-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Setting up your account…' : 'Create account'}
                </button>
              </form>
            </>
          ) : (
            <p className="invite-hint">
              Password sign-up isn&rsquo;t available for this organization. Use Continue with Google
              above.
            </p>
          )}

          <p className="signin-support">{formatExpiry(invite.expiresAt)}</p>
        </div>
      </div>
    </main>
  );
}
